import styled, { css } from "styled-components";
import {
  h3StrongStyles,
  h4Styles,
  minLarge,
  minMedium,
  normalStrongStyles,
  normalStyles,
  semanticGreen,
  shade0,
  shade700,
  shade900,
  smallStyles,
} from "@songtradr-one/components-frontend";
import { staticText } from "../../../statics/static-text";

// TEXT
export const text = staticText.controlCenter.tile;

// CONSTANTS
const CONTAINER_SCALE = 1.05;

// STYLES
const activeStyles = css`
  background-color: ${shade0};
  color: ${shade900};
  transform: scale(${CONTAINER_SCALE});
`;

export const Container = styled.a<ContainerProps>`
  position: relative;
  height: 10.6875rem;
  width: 10.6875rem;
  background-color: ${shade900};
  color: ${shade0};
  text-decoration: none;

  will-change: transform, opacity;
  transform: translateZ(0); /* GPU layer */
  -webkit-transform: translateZ(0); /* iOS Safari */
  backface-visibility: hidden;

  transition:
    transform 0.5s ease-in-out,
    background-color 0.5s,
    color 0.5s;
  transform-origin: center;

  @media (hover: hover) and (pointer: fine) {
    &:hover,
    &:focus-visible {
      cursor: pointer;
      ${activeStyles}
    }
  }

  ${({ $active }) => $active && activeStyles}

  ${({ $disabled }) =>
    $disabled &&
    `
      pointer-events: none;
      color: ${shade700};
    `}

  @media ${minMedium} {
    height: 12rem;
    width: 12rem;
  }

  @media ${minLarge} {
    height: 15rem;
    width: 15rem;
  }
`;

export const InnerContainer = styled.div<InnerContainerProps>`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  transform-origin: center;
  transition: transform 0.5s ease-in-out;
  ${Container}:hover & {
    transform: scale(${1 / CONTAINER_SCALE});
  }
  ${({ $active }) => $active && `transform: scale(${1 / CONTAINER_SCALE});`}
`;

export const LottieAnimationWrapper = styled.div<AnimationWrapperProps>`
  flex: 1 1 auto;
  display: flex;
  align-items: center;

  > * {
    pointer-events: none;
  }

  ${({ $disabled }) =>
    $disabled &&
    `
      opacity: 0.3;
    `}
`;

export const Title = styled.h3`
  transform: translateZ(0);
  flex: 0 0 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  bottom: 0;
  min-height: 3.5rem; // height of two rows of text (to account for wrapping)
  width: 100%;
  margin: 1.5rem 0 0.5rem 0;
  padding: 0 1rem;
  text-align: center;
  ${normalStrongStyles};

  @media ${minMedium} {
    ${h4Styles};
  }

  @media ${minLarge} {
    ${h3StrongStyles};
    margin: 1.5rem 0 1.5rem 0;
  }
`;

export const StatusContainer = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 1rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

export const StatusText = styled.p`
  ${smallStyles};
  color: ${shade0};

  @media ${minMedium} {
    ${normalStyles};
  }
`;

export const Dot = styled.div`
  height: 0.5rem;
  width: 0.5rem;
  background-color: ${semanticGreen};
  border-radius: 100%;
  flex-shrink: 0;

  @media ${minMedium} {
    height: 0.75rem;
    width: 0.75rem;
  }
`;

// DATA

// TYPES
export type Props = {
  route: string;
  title?: string;
  lottieAnimation?: unknown;
  active?: boolean;
  disabled?: boolean;
  onKeyDown?: (ev: React.KeyboardEvent) => void;
};

type ContainerProps = {
  $disabled?: boolean;
  $active?: boolean;
};

type InnerContainerProps = Omit<ContainerProps, "$disabled">;

type AnimationWrapperProps = Pick<ContainerProps, "$disabled">;

// FUNCTIONS

// ASSETS
